// @flow

/**
 * Creates a throttled dispatcher for text streaming
 * Batches APPEND_AGENT_TEXT actions using MessageChannel for reliable scheduling
 * MessageChannel is not throttled by browsers (unlike RAF/setTimeout in background tabs)
 * @param {Function} dispatch - The original dispatch function
 * @param intervalMs
 * @param maxChars
 * @returns {Object} - Object with dispatch function and cleanup
 */
export function createThrottledTextDispatcher(
  dispatch: (action: any) => void,
  {
    intervalMs = 50,      // ~20 updates/sec
    maxChars = 8_000,     // flush if buffer gets big
  } = {}
) {
  const buffers = new Map<string, string[]>();
  const sizes = new Map<string, number>();

  let scheduled = false;
  let lastFlush = 0;

  const channel = new MessageChannel();

  function enqueue(agentMessageId, chunk) {
    if (!buffers.has(agentMessageId)) {
      buffers.set(agentMessageId, []);
      sizes.set(agentMessageId, 0);
    }
    buffers.get(agentMessageId).push(chunk);
    sizes.set(agentMessageId, (sizes.get(agentMessageId) || 0) + chunk.length);
  }

  function flushOne(agentMessageId: string) {
    const parts = buffers.get(agentMessageId);
    if (!parts || parts.length === 0) return;

    const text = parts.join("");
    buffers.set(agentMessageId, []);
    sizes.set(agentMessageId, 0);

    dispatch({
      type: "APPEND_AGENT_TEXT",
      agentMessageId,
      textChunk: text,
    });
  }

  function flushAll() {
    scheduled = false;
    lastFlush = performance.now();

    for (const agentMessageId of buffers.keys()) {
      flushOne(agentMessageId);
    }
  }

  function maybeScheduleFlush() {
    if (scheduled) return;

    const now = performance.now();
    const dueIn = intervalMs - (now - lastFlush);

    scheduled = true;

    if (dueIn <= 0) {
      // flush next macrotask
      channel.port2.postMessage(null);
    } else {
      // “arm” a timed flush, but still execute via MessageChannel when time elapses
      setTimeout(() => channel.port2.postMessage(null), dueIn);
    }
  }

  channel.port1.onmessage = flushAll;

  const throttledDispatch = (action: any) => {
    if (action?.type === "APPEND_AGENT_TEXT") {
      const { agentMessageId, textChunk } = action;
      enqueue(agentMessageId, textChunk);

      // size-based flush (prevents huge memory growth)
      if ((sizes.get(agentMessageId) || 0) >= maxChars) {
        // force soonest possible flush
        lastFlush = 0;
      }
      maybeScheduleFlush();
      return;
    }

    dispatch(action);
  };

  const flush = () => flushAll();

  const cleanup = () => {
    channel.port1.close();
    channel.port2.close();
    buffers.clear();
    sizes.clear();
    scheduled = false;
  };

  return { dispatch: throttledDispatch, flush, cleanup };
}